import { useRef, useState } from "react";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API_BASE, authedFetch, resolveImageUrl } from "../lib/api";
import { useAdminAuth } from "../lib/auth";
import { useSiteImages } from "../lib/siteImages";

/**
 * Renders an <img> that, when the admin is logged in, shows a small
 * "Edit" overlay button. Click → file picker → uploads to object storage →
 * updates the site_images mapping for `imageKey`.
 *
 * Props:
 *   - imageKey: string (must match a backend SITE_IMAGE_DEFAULTS key)
 *   - fallback: string (URL used before context has loaded)
 *   - className, alt, style, ...img attrs
 */
const EditableImage = ({
  imageKey,
  fallback = "",
  className = "",
  alt = "",
  style,
  testId,
  ...imgProps
}) => {
  const { images, updateImage } = useSiteImages();
  const { isAuthed: isAdmin } = useAdminAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const src = images[imageKey] || fallback;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("swk_admin_token");
      const fd = new FormData();
      fd.append("file", file);
      const res = await authedFetch(`${API_BASE}/admin/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      await updateImage(imageKey, data.url);
      toast.success("Image updated");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="editable-image-wrap" style={style} data-testid={testId || `editable-image-${imageKey}`}>
      <img src={resolveImageUrl(src)} alt={alt} className={className} {...imgProps} />
      {isAdmin && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onPick}
            data-testid={`editable-image-input-${imageKey}`}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="editable-image-btn"
            data-testid={`editable-image-btn-${imageKey}`}
          >
            {uploading ? (
              <><Loader2 size={12} className="animate-spin" /> Uploading…</>
            ) : (
              <><Pencil size={12} strokeWidth={2} /> Edit Image</>
            )}
          </button>
        </>
      )}
    </div>
  );
};

export default EditableImage;
